// packages/web/src/process/useProcessLayouts.ts
//
// Memoizes both layouts for the active process. Pure projection of
// (walkResult, swimlaneContext) into { flow, sequence }.

import { useMemo } from "react";
import type {
  BpmnLayout,
  SwimlaneContext,
  SwimlaneLayout,
  WalkResult,
} from "@archlang/engine";
import { bpmnLayout, buildScaffold, swimlaneLayout } from "@archlang/engine";

export interface UseProcessLayoutsArgs {
  readonly walkResult: WalkResult | null;
  /**
   * Walk that materializes every branch (selected + unselected). Every
   * variant uses it so conditional alternatives stay visible (flow/BPMN
   * as gateway bands, sequence as stacked UML-alt bands; unselected rows
   * render dimmed). Falls back to the selected-only walk when not provided.
   */
  readonly walkResultAllBranches?: WalkResult | null;
  readonly swimlaneContext: SwimlaneContext;
}

export interface UseProcessLayoutsResult {
  readonly flow: SwimlaneLayout | null;
  readonly sequence: SwimlaneLayout | null;
  readonly bpmn: BpmnLayout | null;
}

export function useProcessLayouts(
  args: UseProcessLayoutsArgs,
): UseProcessLayoutsResult {
  const { walkResult, walkResultAllBranches, swimlaneContext } = args;
  const flowSource = walkResultAllBranches ?? walkResult;

  const flow = useMemo(() => {
    if (!flowSource) return null;
    return swimlaneLayout(flowSource, swimlaneContext, { variant: "flow" });
  }, [flowSource, swimlaneContext]);

  // Sequence reads the all-branches walk too — alternatives stack as
  // UML-alt bands (unselected dimmed), matching flow's information parity.
  const sequence = useMemo(() => {
    if (!flowSource) return null;
    return swimlaneLayout(flowSource, swimlaneContext, { variant: "sequence" });
  }, [flowSource, swimlaneContext]);

  // BPMN reads the all-branches walk like flow — conditional alternatives
  // stay visible as gateway bands.
  const bpmn = useMemo(() => {
    if (!flowSource) return null;
    return bpmnLayout(buildScaffold(flowSource, swimlaneContext));
  }, [flowSource, swimlaneContext]);

  return { flow, sequence, bpmn };
}
